import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';

import { useTabbar } from '@common/providers/useTabbar';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { Card } from '@common/ui/Card';
import { Input } from '@common/ui/inputs/Input';
import { Heading } from '@common/ui/typography/Heading';
import { useEventEmit } from '@common/utils/events';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { isStorableUser, reportRefusedUser } from '@entities/auth/utils';

import { usePostProfileUpdate } from '@services/hooks/UsersService';

const studyAndWorkSchema = z.object({
  study: z.string().trim().optional(),
  work: z.string().trim().optional(),
});

type FormData = z.infer<typeof studyAndWorkSchema>;

export const StudyAndWork = () => {
  const tabbar = useTabbar();
  const emit = useEventEmit();
  const navigate = useNavigate();

  const { setUser } = useAuthStore();
  const { trigger: triggerPostProfileUpdate } = usePostProfileUpdate();

  const [isSaving, setIsSaving] = useState(false);
  // Holds off a second save before the disabled button renders. Released only
  // when a save fails: a saved form leaves the screen.
  const isSavingRef = useRef(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(studyAndWorkSchema),
  });

  const onSubmit = async (data: FormData) => {
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      const updatedUser = await triggerPostProfileUpdate({
        study_and_work: [data.study, data.work].filter(
          (item) => item !== undefined,
        ),
      });

      if (isStorableUser(updatedUser)) {
        setUser(updatedUser);
        navigate({ to: '/profile/edit' });
        emit('info', {
          title: 'Данные обновлены',
        });
        return;
      }

      // An answer without a user is a failed save: the stored user stays.
      reportRefusedUser('StudyAndWork', updatedUser);
    } catch {
      // A failed request: handled below, as a save without a user is.
    }

    emit('error', {
      title: 'Данные не загрузились',
      description: 'Попробуйте ещё раз',
    });
    isSavingRef.current = false;
    setIsSaving(false);
  };

  useEffect(() => {
    tabbar.setBottomSheet(
      <BottomSheet
        buttons={[
          <Button
            testId="study-and-work-save-button"
            disabled={isSaving}
            loading={isSaving}
            onClick={() => handleSubmit(onSubmit)()}
          >
            Сохранить
          </Button>,
        ]}
      />,
    );
  }, [isSaving]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Heading level={2} align="center">
        Учёба и работа
      </Heading>

      <Card
        testId="profile-study-and-work-form"
        transparent
        style={{ padding: '0 16px' }}
      >
        <Controller
          name="study"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-test-id="study-and-work-study-input"
              placeholder="Место учебы"
              error={errors.study?.message}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value || undefined)}
            />
          )}
        />

        <Controller
          name="work"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-test-id="study-and-work-work-input"
              placeholder="Место работы"
              error={errors.work?.message}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value || undefined)}
            />
          )}
        />
      </Card>
    </form>
  );
};
